/**
 * AppSideNav — Standard sidebar navigation for OpCentral apps.
 * Uses CSS variables from @opcentral/skinnr-lib/theme/opcentral.css
 *
 * Works with or without react-router. If react-router is available,
 * nav items use NavLink for active state. Otherwise falls back to <a>.
 */

import React from 'react';

export interface NavItem {
  /** Route path (e.g., '/documents') */
  path: string;
  /** Display label */
  label: string;
  /** SVG icon as React node, or emoji string */
  icon?: React.ReactNode;
  /** Section: 'main' items appear at top, 'bottom' items at bottom */
  section?: 'main' | 'bottom';
}

export interface AppSideNavProps {
  items: NavItem[];
  collapsed?: boolean;
  /** Called when a nav item is clicked (for non-router usage) */
  onNavigate?: (path: string) => void;
  /** Current active path (for non-router usage) */
  activePath?: string;
}

export function AppSideNav({ items, collapsed = false, onNavigate, activePath }: AppSideNavProps) {
  const mainItems = items.filter((i) => (i.section || 'main') === 'main');
  const bottomItems = items.filter((i) => i.section === 'bottom');

  return (
    <nav
      className={`oc-sidenav ${collapsed ? 'oc-sidenav--collapsed' : ''}`}
      aria-label="Main navigation"
    >
      <div className="oc-sidenav-items">
        {mainItems.map((item) => (
          <NavButton
            key={item.path}
            item={item}
            collapsed={collapsed}
            active={activePath === item.path}
            onClick={() => onNavigate?.(item.path)}
          />
        ))}
      </div>

      {bottomItems.length > 0 && (
        <div className="oc-sidenav-bottom">
          {bottomItems.map((item) => (
            <NavButton
              key={item.path}
              item={item}
              collapsed={collapsed}
              active={activePath === item.path}
              onClick={() => onNavigate?.(item.path)}
            />
          ))}
        </div>
      )}
    </nav>
  );
}

function NavButton({
  item,
  collapsed,
  active,
  onClick,
}: {
  item: NavItem;
  collapsed: boolean;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`oc-sidenav-item ${active ? 'oc-sidenav-item--active' : ''}`}
      onClick={onClick}
      title={collapsed ? item.label : undefined}
      aria-current={active ? 'page' : undefined}
    >
      {item.icon && <span className="oc-sidenav-icon">{item.icon}</span>}
      {!collapsed && <span className="oc-sidenav-label">{item.label}</span>}
    </button>
  );
}
